import { z } from 'zod';

/**
 * ID delle sei caratteristiche di Dungeon World Rebrewed.
 * Il manuale usa anche le abbreviazioni FOR/DES/COS/INT/SAG/CAR per i tiri.
 */
export const StatIdSchema = z.enum([
  'forza',
  'destrezza',
  'costituzione',
  'intelligenza',
  'saggezza',
  'carisma',
]);
export type StatId = z.infer<typeof StatIdSchema>;

/** Lista ordinata degli ID delle caratteristiche (utile per UI / iterazione). */
export const STAT_IDS = StatIdSchema.options;

/**
 * Punteggio "raw" di una caratteristica (3..18).
 * È il dato persistente della scheda PG. Il modificatore si DERIVA con
 * `scoreToModifier`. Non duplicare entrambi nella stessa entità.
 */
export const StatScoreSchema = z.number().int().min(3).max(18);
export type StatScore = z.infer<typeof StatScoreSchema>;

/**
 * Modificatore di caratteristica derivato dal punteggio.
 * Range valido in DWR: -3..+3.
 */
export const StatModifierSchema = z.number().int().min(-3).max(3);
export type StatModifier = z.infer<typeof StatModifierSchema>;

/**
 * Mappa completa delle 6 caratteristiche del PG.
 * Memorizza i punteggi (3..18); i modificatori sono derivati.
 */
export const StatsSchema = z.object({
  forza: StatScoreSchema,
  destrezza: StatScoreSchema,
  costituzione: StatScoreSchema,
  intelligenza: StatScoreSchema,
  saggezza: StatScoreSchema,
  carisma: StatScoreSchema,
});
export type Stats = z.infer<typeof StatsSchema>;

/**
 * Converte un punteggio (3..18) nel relativo modificatore (-3..+3)
 * secondo il mapping di Dungeon World classico, riportato anche nel
 * manuale draft DWR (riga ~1100 ca, tabella punteggio→modificatore).
 *
 *   3       → -3
 *   4..5    → -2
 *   6..8    → -1
 *   9..12   →  0
 *   13..15  → +1
 *   16..17  → +2
 *   18      → +3
 */
export function scoreToModifier(score: StatScore): StatModifier {
  if (score === 3) return -3;
  if (score <= 5) return -2;
  if (score <= 8) return -1;
  if (score <= 12) return 0;
  if (score <= 15) return 1;
  if (score <= 17) return 2;
  return 3;
}

/** Calcola tutti i 6 modificatori a partire dai 6 punteggi. */
export function statsToModifiers(stats: Stats): Record<StatId, StatModifier> {
  return {
    forza: scoreToModifier(stats.forza),
    destrezza: scoreToModifier(stats.destrezza),
    costituzione: scoreToModifier(stats.costituzione),
    intelligenza: scoreToModifier(stats.intelligenza),
    saggezza: scoreToModifier(stats.saggezza),
    carisma: scoreToModifier(stats.carisma),
  };
}
